import { ReplayEventSource } from './ReplayEventSource';
import { RecordingEventSource } from './RecordingEventSource';
import type { RecordedEvent, Recording, StreamKey } from './types';

const FIXTURE_BASE = '/fixtures/onboarding';

type ReplayMode = 'off' | 'record' | 'replay';

interface Params {
  mode: ReplayMode;
  name: string;
  speed: number;
}

let params: Params | null = null;
let recording: Recording | null = null;
let loading: Promise<void> | null = null;
let startedAt = 0;

const captured: RecordedEvent[] = [];
const capturedRest: Record<string, unknown> = {};

function readParams(): Params {
  if (params) return params;
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    params = { mode: 'off', name: '', speed: 1 };
    return params;
  }
  const search = new URLSearchParams(window.location.search);
  const replay = search.get('replay');
  const record = search.get('record');
  const rawSpeed = search.has('speed') ? Number(search.get('speed')) : 1;
  const speed = Number.isFinite(rawSpeed) ? rawSpeed : 1;

  if (replay) params = { mode: 'replay', name: replay, speed };
  else if (record) params = { mode: 'record', name: record === '1' ? 'onboarding' : record, speed: 1 };
  else params = { mode: 'off', name: '', speed: 1 };

  return params;
}

export function replayMode(): ReplayMode {
  return readParams().mode;
}

export function isReplayActive(): boolean {
  return replayMode() === 'replay';
}

export function isRecordActive(): boolean {
  return replayMode() === 'record';
}

export function whenReady(): Promise<void> {
  if (!isReplayActive()) return Promise.resolve();
  if (!loading) {
    const { name } = readParams();
    loading = fetch(`${FIXTURE_BASE}/${name}.json`)
      .then(res => {
        if (!res.ok) throw new Error(`Replay recording "${name}" not found (${res.status})`);
        return res.json() as Promise<Recording>;
      })
      .then(loaded => { recording = loaded; });
  }
  return loading;
}

function streamKeyFor(url: string): StreamKey {
  return url.includes('/onboarding-runs/') ? 'run' : 'scan';
}

function sink(stream: StreamKey, type: string, data: string): void {
  if (!startedAt) startedAt = Date.now();
  captured.push({ atMs: Date.now() - startedAt, stream, type, data });
}

export interface StreamOptions {
  secondary?: boolean;
}

export function openOnboardingStream(
  url: string,
  init?: EventSourceInit,
  options: StreamOptions = {},
): EventSource {
  const { mode, speed } = readParams();
  const stream = streamKeyFor(url);

  if (mode === 'replay') {
    const events = options.secondary
      ? []
      : (recording?.events ?? []).filter(event => event.stream === stream);
    return new ReplayEventSource(url, events, speed) as unknown as EventSource;
  }
  if (mode === 'record' && !options.secondary) {
    if (!startedAt) startedAt = Date.now();
    installRecorderConsole();
    return new RecordingEventSource(url, init, stream, sink) as unknown as EventSource;
  }
  return new EventSource(url, init);
}

export async function replayRest<T>(key: string, real: () => Promise<T>): Promise<T> {
  const { mode } = readParams();

  if (mode === 'replay') {
    await whenReady();
    if (!recording || !(key in recording.rest)) {
      throw new Error(`Replay recording has no stored response for "${key}"`);
    }
    return recording.rest[key] as T;
  }
  if (mode === 'record') {
    const value = await real();
    capturedRest[key] = value;
    return value;
  }
  return real();
}

export function buildRecording(): Recording {
  return {
    site: String((capturedRest['scans:create'] as { websiteUrl?: string } | undefined)?.websiteUrl ?? ''),
    recordedAt: new Date().toISOString(),
    rest: capturedRest,
    events: captured,
  };
}

export function installRecorderConsole(): void {
  if (!import.meta.env.DEV || !isRecordActive() || typeof window === 'undefined') return;
  const { name } = readParams();
  const api = {
    save: () => {
      const blob = new Blob([JSON.stringify(buildRecording())], { type: 'application/json' });
      const anchor = document.createElement('a');
      anchor.href = URL.createObjectURL(blob);
      anchor.download = `${name}.json`;
      anchor.click();
      URL.revokeObjectURL(anchor.href);
    },
    dump: () => buildRecording(),
    count: () => captured.length,
  };
  (window as unknown as { obReplay: typeof api }).obReplay = api;
}
